import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";

import { getMyServices, deleteService, getHostBookings, cancelBooking, getMyWallet, completeBooking } from "apis";
import { Loading } from "components";
import { useAppModal } from "components/common/AppModalProvider";
import StudioWallet from "pages/Studio/StudioWallet";
import { ServiceDTO } from "types/DTOs/service/service.dto";
import { BookingDTO } from "types/DTOs/booking/booking.dto";
import { BookingStatus } from "types";
import { getImageUrl } from "utils/image.util";

type SellerTab = "dashboard" | "services" | "bookings" | "wallet";

const statusConfig: Record<string, { label: string; color: string }> = {
  [BookingStatus.PENDING]: { label: "대기", color: "bg-yellow-100 text-yellow-700" },
  [BookingStatus.PAID]: { label: "결제완료", color: "bg-green-100 text-green-700" },
  [BookingStatus.COMPLETED]: { label: "완료", color: "bg-achromatic-100 text-achromatic-500" },
  [BookingStatus.CANCELLED]: { label: "취소", color: "bg-red-100 text-red-500" },
};

const SellerDashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showAlert, showConfirm } = useAppModal();
  const [tab, setTab] = useState<SellerTab>("dashboard");
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const { data: servicesData, isLoading: servicesLoading } = useQuery("myServices", getMyServices);
  const { data: bookingsData, isLoading: bookingsLoading } = useQuery("hostBookings", getHostBookings);
  const { data: walletData } = useQuery(["myWallet"], () => getMyWallet());

  const walletBalance = walletData?.data?.data?.klav ?? 0;
  const allServices: ServiceDTO[] = servicesData?.data?.data ?? [];
  const services = allServices.filter(s => s.isActive);
  const bookings: BookingDTO[] = bookingsData?.data?.data ?? [];

  // Revenue stats (플랫폼 수수료 15% 제외 정산 수익)
  const completedBookingsList = bookings.filter(b => b.status === BookingStatus.COMPLETED);
  const totalGrossRevenue = completedBookingsList.reduce((sum, b) => sum + (b.paidAmount || 0), 0);
  const totalNetRevenue = Math.floor(totalGrossRevenue * 0.85);
  const pendingBookings = bookings.filter(b => b.status === BookingStatus.PAID).length;
  const completedBookings = completedBookingsList.length;
  const activeServices = services.filter(s => s.isActive).length;

  const handleDeleteService = useCallback(async (id: string) => {
    if (!(await showConfirm("이 서비스를 삭제하시겠습니까?\n삭제된 서비스는 더 이상 노출되지 않습니다.", { title: '서비스 삭제', confirmText: '삭제', variant: 'warning' }))) return;
    try {
      await deleteService(id);
      queryClient.invalidateQueries("myServices");
      showAlert("서비스가 삭제되었습니다.", { variant: 'success' });
    } catch (error) {
      console.error(error);
      showAlert("삭제 중 오류가 발생했습니다.", { variant: 'error' });
    }
  }, [queryClient, showAlert, showConfirm]);

  const handleComplete = useCallback(async (bookingId: string) => {
    try {
      await completeBooking(bookingId);
      queryClient.invalidateQueries("hostBookings");
    } catch (error) {
      console.error(error);
      showAlert("완료 처리 중 오류가 발생했습니다.", { variant: 'error' });
    }
  }, [queryClient, showAlert]);

  const handleCancelConfirm = useCallback(async (reason: string) => {
    if (!cancelTargetId) return;
    setIsCancelling(true);
    try {
      await cancelBooking(cancelTargetId, reason);
      queryClient.invalidateQueries("hostBookings");
      setCancelTargetId(null);
      showAlert("예약이 취소되었습니다. 게스트에게 환불이 진행됩니다.", { variant: 'success' });
    } catch (error: any) {
      const msg = error?.response?.data?.message || "취소 처리 중 오류가 발생했습니다.";
      showAlert(msg, { variant: 'error' });
    } finally {
      setIsCancelling(false);
    }
  }, [cancelTargetId, queryClient, showAlert]);

  const isLoading = servicesLoading || bookingsLoading;

  const tabs: { key: SellerTab; label: string; icon: string }[] = [
    { key: "dashboard", label: "대시보드", icon: "📊" },
    { key: "services", label: "내 서비스", icon: "📦" },
    { key: "bookings", label: "예약 관리", icon: "📋" },
    { key: "wallet", label: "정산/지갑", icon: "💰" },
  ];

  return (
    <>
    <div className="flex flex-col gap-4">
      {/* Sub-tabs */}
      <div className="flex gap-1.5 bg-achromatic-50 rounded-xl p-1">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[12px] font-bold transition-all ${
              tab === t.key
                ? "bg-white text-achromatic-900 shadow-sm"
                : "text-achromatic-400 hover:text-achromatic-600"
            }`}
          >
            <span>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && <div className="p-6"><Loading /></div>}

      {/* ── Dashboard Tab ── */}
      {!isLoading && tab === "dashboard" && (
        <div className="flex flex-col gap-4">
          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3 mobile:grid-cols-2">
            <div className="bg-gradient-to-br from-primary-500 to-violet-600 rounded-xl p-4 text-white">
              <div className="flex justify-between items-start">
                <p className="text-[11px] text-white/80 font-medium">총 정산 수익</p>
                <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded text-white font-medium">Net 85%</span>
              </div>
              <p className="text-xl font-extrabold mt-1">{totalNetRevenue.toLocaleString()}<span className="text-[12px] font-medium ml-0.5">원</span></p>
              <p className="text-[10px] text-white/70 mt-1 truncate">총 매출 {totalGrossRevenue.toLocaleString()}원 (수수료 15% 차감)</p>
            </div>

            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 text-white">
              <div className="flex justify-between items-center">
                <p className="text-[11px] text-white/80 font-medium">출금 가능 잔액</p>
                <button
                  onClick={() => setTab("wallet")}
                  className="text-[10px] bg-white text-emerald-700 font-bold px-2 py-0.5 rounded hover:bg-emerald-50 transition-all shadow-sm"
                >
                  환전 신청 →
                </button>
              </div>
              <p className="text-xl font-extrabold mt-1">{walletBalance.toLocaleString()}<span className="text-[12px] font-medium ml-0.5">원</span></p>
              <p className="text-[10px] text-white/70 mt-1 truncate">최소 출금액 10,000원</p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-achromatic-50">
              <p className="text-[11px] text-achromatic-400 font-medium">등록 서비스</p>
              <p className="text-xl font-extrabold text-achromatic-900 mt-1">{activeServices}<span className="text-[12px] font-medium text-achromatic-400 ml-0.5">개</span></p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-achromatic-50">
              <p className="text-[11px] text-achromatic-400 font-medium">완료 거래 / 진행 중</p>
              <p className="text-xl font-extrabold text-achromatic-900 mt-1">{completedBookings}<span className="text-[12px] font-medium text-achromatic-400 ml-0.5">건</span> <span className="text-xs text-green-600 font-bold ml-1">({pendingBookings}건 대기)</span></p>
            </div>
          </div>

          {/* Recent bookings preview */}
          {bookings.length > 0 && (
            <div className="bg-white rounded-xl border border-achromatic-50">
              <div className="flex justify-between items-center px-4 pt-4 pb-2">
                <h3 className="text-[13px] font-bold text-achromatic-900">최근 예약</h3>
                <button onClick={() => setTab("bookings")} className="text-[11px] text-primary-500 font-semibold">전체보기 →</button>
              </div>
              <div className="flex flex-col divide-y divide-achromatic-50">
                {bookings.slice(0, 3).map(b => {
                  const st = statusConfig[b.status] ?? statusConfig[BookingStatus.PENDING];
                  return (
                    <div key={b.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-achromatic-900 truncate">{b.service?.title ?? "서비스"}</p>
                        <p className="text-[11px] text-achromatic-400">{b.guest?.name ?? "게스트"} · {b.schedule?.date ?? "-"}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[12px] font-bold text-achromatic-700">{(b.paidAmount || 0).toLocaleString()}원</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${st.color}`}>{st.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick actions */}
          <div className="flex gap-3">
            <button onClick={() => navigate("/service/new")}
              className="flex-1 py-3 bg-gradient-to-r from-primary-500 to-violet-500 text-white rounded-xl text-[13px] font-bold hover:shadow-lg transition-all">
              + 새 서비스 등록
            </button>
            <button onClick={() => setTab("services")}
              className="flex-1 py-3 bg-white text-achromatic-600 rounded-xl text-[13px] font-semibold border border-achromatic-100 hover:bg-achromatic-50 transition-all">
              📦 서비스 관리
            </button>
          </div>
        </div>
      )}

      {/* ── Services Tab ── */}
      {!isLoading && tab === "services" && (
        <div className="flex flex-col gap-3">
          {services.length === 0 ? (
            <div className="flex flex-col items-center py-16 gap-3 text-achromatic-300">
              <div className="text-4xl">📦</div>
              <p className="font-medium text-achromatic-600">등록된 서비스가 없습니다</p>
              <button onClick={() => navigate("/service/new")}
                className="mt-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl text-[13px] font-bold hover:bg-primary-600 transition-colors">
                + 첫 서비스 등록하기
              </button>
            </div>
          ) : (
            services.map(service => {
              const totalSlots = service.schedules?.length ?? 0;
              const bookedSlots = service.schedules?.filter(s => s.isBooked).length ?? 0;
              const availableSlots = totalSlots - bookedSlots;

              return (
                <div key={service.id} className="bg-white rounded-xl border border-achromatic-50 overflow-hidden">
                  <div className="flex mobile:flex-col">
                    {/* Image */}
                    <div className="w-[120px] h-[100px] flex-shrink-0 bg-achromatic-50 mobile:w-full mobile:h-[140px]">
                      {service.images && service.images.length > 0 ? (
                        <img
                          src={getImageUrl(service.images[0])}
                          alt={service.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl bg-achromatic-50">📦</div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[14px] font-bold text-achromatic-900 truncate">{service.title}</h3>
                          <p className="text-[12px] text-achromatic-400 mt-0.5 line-clamp-1">{service.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-achromatic-400">
                        <span className="font-bold text-achromatic-800">{service.price.toLocaleString()}원</span>
                        <span>·</span>
                        <span>{service.durationMinutes}분</span>
                        <span>·</span>
                        <span className={availableSlots > 0 ? "text-primary-500" : "text-achromatic-300"}>
                          {availableSlots > 0 ? `${availableSlots}/${totalSlots}개 가능` : totalSlots > 0 ? "모두 예약됨" : "시간 미등록"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex border-t border-achromatic-50">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/service/${service.id}`, { state: { from: '/mypage?tab=listings' } }); }}
                      className="flex-1 py-2.5 text-[12px] font-semibold text-achromatic-500 hover:bg-achromatic-50 transition-colors"
                    >
                      👁 미리보기
                    </button>
                    <div className="w-px bg-achromatic-50" />
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/service/${service.id}/edit`); }}
                      className="flex-1 py-2.5 text-[12px] font-semibold text-primary-500 hover:bg-primary-50 transition-colors"
                    >
                      ✏️ 수정
                    </button>
                    <div className="w-px bg-achromatic-50" />
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteService(service.id); }}
                      className="flex-1 py-2.5 text-[12px] font-semibold text-red-400 hover:bg-red-50 transition-colors"
                    >
                      🗑 삭제
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Bookings Tab ── */}
      {!isLoading && tab === "bookings" && (
        <div className="flex flex-col gap-3">
          {bookings.length === 0 ? (
            <div className="flex flex-col items-center py-16 gap-3 text-achromatic-300">
              <div className="text-4xl">📋</div>
              <p className="font-medium text-achromatic-600">예약 내역이 없습니다</p>
              <p className="text-[12px]">게스트가 서비스를 예약하면 여기에 표시됩니다</p>
            </div>
          ) : (
            bookings.map(b => {
              const st = statusConfig[b.status] ?? statusConfig[BookingStatus.PENDING];
              return (
                <div key={b.id} className="bg-white rounded-xl border border-achromatic-50 overflow-hidden">
                  <div className="flex justify-between items-start p-4 pb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[14px] font-semibold text-achromatic-900 truncate">{b.service?.title ?? "서비스"}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {b.guest?.profileImage ? (
                          <img src={getImageUrl(b.guest.profileImage)} alt="" className="w-5 h-5 rounded-full object-cover" />
                        ) : (
                          <div className="w-5 h-5 bg-achromatic-200 rounded-full" />
                        )}
                        <span className="text-[12px] text-achromatic-400">{b.guest?.name ?? "게스트"}</span>
                      </div>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-1 rounded-md ${st.color}`}>{st.label}</span>
                  </div>

                  <div className="flex items-center gap-4 px-4 py-2 text-[12px] text-achromatic-500">
                    <span>📅 {b.schedule?.date ?? "-"}</span>
                    <span>⏰ {b.schedule?.startTime?.slice(0, 5) ?? "--:--"} ~ {b.schedule?.endTime?.slice(0, 5) ?? "--:--"}</span>
                    <span className="text-primary-500 font-semibold">{(b.paidAmount || 0).toLocaleString()}원</span>
                  </div>

                  <div className="flex gap-2 px-4 py-3 border-t border-achromatic-50 bg-achromatic-50/30">
                    {b.status === BookingStatus.PAID && (
                      <>
                        <button
                          onClick={() => handleComplete(b.id)}
                          disabled={b.completedByHost}
                          className={`flex-1 py-2 rounded-lg text-[12px] font-semibold transition-colors ${
                            b.completedByHost
                              ? "bg-achromatic-100 text-achromatic-300"
                              : "bg-primary-500 text-white hover:bg-primary-600"
                          }`}
                        >
                          {b.completedByHost ? "✅ 완료 확인됨" : "서비스 완료 확인"}
                        </button>
                        <button
                          onClick={() => setCancelTargetId(b.id)}
                          className="flex-1 py-2 rounded-lg text-[12px] font-semibold bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                        >
                          예약 취소
                        </button>
                      </>
                    )}
                    {b.status === BookingStatus.COMPLETED && (
                      <div className="flex flex-col gap-1 flex-1">
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-achromatic-400">결제금액</span>
                          <span className="text-achromatic-700 font-semibold">{(b.paidAmount || 0).toLocaleString()}원</span>
                        </div>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-achromatic-400">수수료 (15%)</span>
                          <span className="text-red-400">-{Math.floor((b.paidAmount || 0) * 0.15).toLocaleString()}원</span>
                        </div>
                        <div className="flex items-center justify-between text-[11px] pt-1 border-t border-achromatic-100">
                          <span className="text-green-600 font-bold">정산금액</span>
                          <span className="text-green-600 font-bold">{Math.floor((b.paidAmount || 0) * 0.85).toLocaleString()}원</span>
                        </div>
                      </div>
                    )}
                    {b.status === BookingStatus.CANCELLED && (
                      <div className="flex flex-col gap-1">
                        <span className="text-[12px] text-achromatic-400">❌ 취소됨</span>
                        {b.cancelReason && (
                          <span className="text-[11px] text-achromatic-300">사유: {b.cancelReason}</span>
                        )}
                      </div>
                    )}
                    {b.status === BookingStatus.PENDING && <span className="text-[12px] text-achromatic-400 py-2">⏳ 결제 대기</span>}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Wallet / Payout Tab ── */}
      {!isLoading && tab === "wallet" && (
        <StudioWallet />
      )}
    </div>
      <CancelReasonModal
        isOpen={!!cancelTargetId}
        onClose={() => setCancelTargetId(null)}
        onConfirm={handleCancelConfirm}
        isLoading={isCancelling}
      />
    </>
  );
};

export default SellerDashboard;

/* ── Cancel Reason Modal ── */
const CANCEL_PRESETS = [
  "급격한 컨디션 저하 및 건강 문제",
  "갑작스러운 회사 업무/야근 발생",
  "인터넷 및 통신 기기 장애",
];

const CancelReasonModal = ({ isOpen, onClose, onConfirm, isLoading }: {
  isOpen: boolean; onClose: () => void; onConfirm: (reason: string) => void; isLoading: boolean;
}) => {
  const [selected, setSelected] = useState<number | null>(null);
  const [customReason, setCustomReason] = useState("");

  if (!isOpen) return null;

  const isCustom = selected === CANCEL_PRESETS.length;
  const canSubmit = selected !== null && (!isCustom || customReason.trim().length > 0);
  const finalReason = isCustom ? customReason.trim() : (selected !== null ? CANCEL_PRESETS[selected] : "");

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-[400px] mx-4 animate-fadeIn" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-base font-extrabold text-black">예약 취소 사유</h3>
          <p className="text-xs text-achromatic-400 mt-1">취소 사유를 선택해주세요. 게스트에게 전달됩니다.</p>
        </div>
        <div className="flex flex-col gap-2 px-5 pb-3">
          {CANCEL_PRESETS.map((preset, i) => (
            <label key={i} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
              selected === i ? "border-red-400 bg-red-50" : "border-achromatic-100 hover:bg-achromatic-50"
            }`}>
              <input type="radio" name="cancel-reason" checked={selected === i} onChange={() => setSelected(i)} className="accent-red-500" />
              <span className="text-sm text-achromatic-700">{preset}</span>
            </label>
          ))}
          <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
            isCustom ? "border-red-400 bg-red-50" : "border-achromatic-100 hover:bg-achromatic-50"
          }`}>
            <input type="radio" name="cancel-reason" checked={isCustom} onChange={() => setSelected(CANCEL_PRESETS.length)} className="accent-red-500" />
            <span className="text-sm text-achromatic-700">기타 (직접 입력)</span>
          </label>
          {isCustom && (
            <input className="px-3 py-2 border border-achromatic-200 rounded-xl text-sm focus:outline-none focus:border-red-300"
              placeholder="취소 사유를 입력하세요 (30자 이내)" maxLength={30} value={customReason}
              onChange={(e) => setCustomReason(e.target.value)} autoFocus />
          )}
        </div>
        <div className="bg-yellow-50 mx-5 mb-3 px-3 py-2 rounded-lg">
          <p className="text-[11px] text-yellow-700">⚠️ 호스트 취소는 누적 기록되며, 3회 이상 시 서비스 노출이 제한됩니다. 취소 시 게스트에게 100% 환불이 즉시 진행됩니다.</p>
        </div>
        <div className="flex gap-2 px-5 pb-5">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl text-sm font-semibold bg-achromatic-100 text-achromatic-500 hover:bg-achromatic-200 transition-colors">돌아가기</button>
          <button onClick={() => canSubmit && onConfirm(finalReason)} disabled={!canSubmit || isLoading}
            className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors ${
              canSubmit && !isLoading ? "bg-red-500 text-white hover:bg-red-600" : "bg-achromatic-100 text-achromatic-300"
            }`}>
            {isLoading ? "취소 처리 중..." : "취소하기"}
          </button>
        </div>
      </div>
    </div>
  );
};
